import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  GraduationCap,
  Camera,
  Plus,
  Pencil,
  Trash2,
  Phone,
  MapPin,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import {
  listTeachers,
  saveTeacher,
  deleteTeacher,
} from "@/lib/api/teachers.functions";

export const Route = createFileRoute("/_authenticated/m/cadastro-professor")({
  component: TeachersPage,
});

type Teacher = {
  id: string;
  name: string;
  cpf: string | null;
  phone: string | null;
  photo_url: string | null;
  cep: string | null;
  street: string | null;
  number: string | null;
  complement: string | null;
  neighborhood: string | null;
  city: string | null;
  state: string | null;
};

const maskCPF = (v: string) =>
  v.replace(/\D/g, "").slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");

const maskPhone = (v: string) => {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 10) return d.replace(/(\d{2})(\d{4})(\d{0,4})/, "($1) $2-$3").trim();
  return d.replace(/(\d{2})(\d{5})(\d{0,4})/, "($1) $2-$3").trim();
};

const maskCEP = (v: string) => v.replace(/\D/g, "").slice(0, 8).replace(/(\d{5})(\d)/, "$1-$2");

function TeachersPage() {
  const list = useServerFn(listTeachers);
  const save = useServerFn(saveTeacher);
  const del = useServerFn(deleteTeacher);

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [tcId, setTcId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Teacher | null>(null);

  const reload = async () => {
    setLoading(true);
    try {
      const res = await list();
      setTeachers(res.teachers as Teacher[]);
      setTcId(res.tcId);
    } catch (e: any) {
      toast.error(e?.message || "Erro ao carregar professores");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  const filtered = teachers.filter((t) =>
    t.name.toLowerCase().includes(search.toLowerCase()),
  );

  const onDelete = async (id: string) => {
    if (!confirm("Excluir este professor?")) return;
    try {
      await del({ data: { id } });
      toast.success("Professor excluído");
      reload();
    } catch (e: any) {
      toast.error(e?.message || "Erro ao excluir");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <GraduationCap className="w-7 h-7 text-primary" /> Corpo Docente
          </h1>
          <p className="text-muted-foreground text-sm">
            Gerencie e cadastre os professores do seu centro de treinamento.
          </p>
        </div>
        <Button onClick={() => { setEditing(null); setModalOpen(true); }}>
          <Plus className="w-4 h-4 mr-2" /> Novo Professor
        </Button>
      </div>

      <div className="relative w-full sm:w-80">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Buscar professor..."
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-[180px] bg-muted/50 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center border-dashed">
            <GraduationCap className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-40" />
            <h3 className="text-lg font-medium">Nenhum professor cadastrado ainda</h3>
            <p className="text-muted-foreground mt-1 text-sm">
              Clique em "Novo Professor" para começar.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((t) => (
            <Card key={t.id} className="overflow-hidden hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <Avatar className="h-16 w-16">
                    {t.photo_url && <AvatarImage src={t.photo_url} alt={t.name} />}
                    <AvatarFallback className="text-xl font-bold bg-primary/10 text-primary">
                      {t.name.charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-lg leading-tight truncate">{t.name}</h3>
                    {t.cpf && (
                      <p className="text-xs text-muted-foreground mt-0.5">CPF {t.cpf}</p>
                    )}
                    {t.phone && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {t.phone}
                      </p>
                    )}
                    {(t.city || t.state) && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {[t.city, t.state].filter(Boolean).join("/")}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
                  <Button variant="ghost" size="sm" onClick={() => { setEditing(t); setModalOpen(true); }}>
                    <Pencil className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => onDelete(t.id)}>
                    <Trash2 className="w-4 h-4 mr-1" /> Excluir
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <TeacherFormDialog
        key={editing?.id ?? "new"}
        open={modalOpen}
        onOpenChange={setModalOpen}
        editing={editing}
        tcId={tcId}
        onSave={async (payload) => {
          await save({ data: payload });
          toast.success(editing ? "Professor atualizado" : "Professor cadastrado");
          setModalOpen(false);
          reload();
        }}
      />
    </div>
  );
}

function TeacherFormDialog({
  open, onOpenChange, editing, tcId, onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  editing: Teacher | null;
  tcId: string | null;
  onSave: (payload: any) => Promise<void>;
}) {
  const [name, setName] = useState(editing?.name ?? "");
  const [cpf, setCpf] = useState(editing?.cpf ?? "");
  const [phone, setPhone] = useState(editing?.phone ?? "");
  const [photoUrl, setPhotoUrl] = useState<string | null>(editing?.photo_url ?? null);
  const [cep, setCep] = useState(editing?.cep ?? "");
  const [street, setStreet] = useState(editing?.street ?? "");
  const [number, setNumber] = useState(editing?.number ?? "");
  const [complement, setComplement] = useState(editing?.complement ?? "");
  const [neighborhood, setNeighborhood] = useState(editing?.neighborhood ?? "");
  const [city, setCity] = useState(editing?.city ?? "");
  const [state, setState] = useState(editing?.state ?? "");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cepLoading, setCepLoading] = useState(false);

  const handleCepBlur = async () => {
    const clean = cep.replace(/\D/g, "");
    if (clean.length !== 8) return;
    try {
      setCepLoading(true);
      const r = await fetch(`https://viacep.com.br/ws/${clean}/json/`);
      const j = await r.json();
      if (j.erro) return;
      setStreet(j.logradouro ?? "");
      setNeighborhood(j.bairro ?? "");
      setCity(j.localidade ?? "");
      setState(j.uf ?? "");
    } catch { /* ignore */ } finally { setCepLoading(false); }
  };

  const handlePhoto = async (file: File) => {
    if (!tcId) { toast.error("Cadastre o CT primeiro"); return; }
    try {
      setUploading(true);
      const ext = (file.name.split(".").pop() ?? "jpg").toLowerCase();
      const path = `${tcId}/teachers/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("avatars").upload(path, file, { contentType: file.type });
      if (error) throw error;
      const { data, error: sErr } = await supabase.storage.from("avatars").createSignedUrl(path, 60 * 60 * 24 * 365);
      if (sErr) throw sErr;
      setPhotoUrl(data.signedUrl);
    } catch (e: any) {
      toast.error(e?.message ?? "Erro no upload");
    } finally { setUploading(false); }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Nome obrigatório"); return; }
    setSaving(true);
    try {
      await onSave({
        id: editing?.id ?? null,
        name: name.trim(),
        cpf: cpf || null,
        phone: phone || null,
        photo_url: photoUrl,
        cep: cep || null,
        street: street || null,
        number: number || null,
        complement: complement || null,
        neighborhood: neighborhood || null,
        city: city || null,
        state: state || null,
      });
    } catch (e: any) {
      toast.error(e?.message || "Erro ao salvar");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Editar Professor" : "Cadastrar Professor"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-5 pt-2">
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              {photoUrl && <AvatarImage src={photoUrl} />}
              <AvatarFallback className="bg-primary/10 text-primary">
                <Camera className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>
            <div>
              <Label className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-md border bg-background hover:bg-accent text-sm">
                <Camera className="w-4 h-4" />
                {uploading ? "Enviando..." : photoUrl ? "Trocar Foto" : "Escolher Foto"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files?.[0] && handlePhoto(e.target.files[0])}
                />
              </Label>
              <p className="text-xs text-muted-foreground mt-2">Foto do professor (opcional)</p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Nome Completo *</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: João da Silva" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>CPF</Label>
              <Input value={cpf} onChange={(e) => setCpf(maskCPF(e.target.value))} placeholder="000.000.000-00" />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp / Telefone</Label>
              <Input value={phone} onChange={(e) => setPhone(maskPhone(e.target.value))} placeholder="(00) 00000-0000" />
            </div>
          </div>

          <div className="pt-2 border-t">
            <h4 className="font-semibold text-sm mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" /> Endereço
            </h4>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>CEP</Label>
                  <Input
                    value={cep}
                    onChange={(e) => setCep(maskCEP(e.target.value))}
                    onBlur={handleCepBlur}
                    placeholder="00000-000"
                  />
                  {cepLoading && <p className="text-xs text-muted-foreground">Buscando endereço...</p>}
                </div>
                <div className="sm:col-span-2 space-y-2">
                  <Label>Logradouro</Label>
                  <Input value={street} onChange={(e) => setStreet(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Número</Label>
                  <Input value={number} onChange={(e) => setNumber(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Complemento</Label>
                  <Input value={complement} onChange={(e) => setComplement(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Bairro</Label>
                  <Input value={neighborhood} onChange={(e) => setNeighborhood(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label>Cidade</Label>
                  <Input value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>UF</Label>
                  <Input maxLength={2} value={state} onChange={(e) => setState(e.target.value.toUpperCase())} />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={saving || uploading}>
              {saving ? "Salvando..." : editing ? "Salvar Alterações" : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
